import React, { useMemo } from "react";
import DOMPurify from "dompurify";

// tiny classnames merger
function cx(...arr) { return arr.filter(Boolean).join(" "); }

/**
 * Renders rich HTML safely with Tailwind Typography styles.
 *
 * Props:
 * - html: string
 * - className: extra classes (e.g., "prose-lg dark:prose-invert")
 * - allowIframes: boolean (default false) – YT/Vimeo only (whitelist below)
 * - allowInlineStyles: boolean (default false)
 * - allowClasses: boolean (default false) – allow incoming class="" from CMS
 * - iframeWhitelist: string[] – extra allowed hosts
 */
export default function RichContent({
  html = "",
  className,
  allowIframes = false,
  allowInlineStyles = false,
  allowClasses = false,
  iframeWhitelist = ["www.youtube.com", "youtube.com", "player.vimeo.com", "vimeo.com"],
}) {
  // normalize a couple of editor quirks
  const normalized = useMemo(
    () => (html || "").replace(/<br\s*>\s*<\/br>/gi, "<br>"),
    [html]
  );

  // build DOMPurify config
  const purifyConfig = useMemo(() => {
    const cfg = { USE_PROFILES: { html: true } };
    cfg.ADD_ATTR = [...(cfg.ADD_ATTR || []), "loading", "referrerpolicy"];
    if (allowInlineStyles) cfg.ADD_ATTR.push("style");
    if (allowClasses) cfg.ADD_ATTR.push("class");
    if (allowIframes) {
      cfg.ADD_TAGS = [...(cfg.ADD_TAGS || []), "iframe"];
      cfg.ADD_ATTR.push("allow", "allowfullscreen", "frameborder", "scrolling");
    }
    return cfg;
  }, [allowInlineStyles, allowClasses, allowIframes]);

  const safeHtml = useMemo(() => {
    let hookFn = null;

    if (allowIframes) {
      // keep iframes only from whitelisted hosts
      hookFn = (node, data) => {
        if (data.tagName === "iframe") {
          const src = node.getAttribute("src") || "";
          try {
            const u = new URL(src, window.location.origin);
            const ok = iframeWhitelist.some(h => u.hostname === h || u.hostname.endsWith(`.${h}`));
            if (!ok) { node.remove(); return; }
            node.setAttribute("loading", node.getAttribute("loading") || "lazy");
            node.setAttribute("referrerpolicy", node.getAttribute("referrerpolicy") || "no-referrer");
          } catch { node.remove(); }
        }
      };
      DOMPurify.addHook("uponSanitizeElement", hookFn);
    }

    const out = DOMPurify.sanitize(normalized, purifyConfig);

    if (hookFn) DOMPurify.removeHook("uponSanitizeElement");
    return out;
  }, [normalized, purifyConfig, allowIframes, iframeWhitelist]);

  // Tailwind Typography “prose” + useful element variants
  const proseDefaults =
    "prose prose-slate max-w-none " +
    "prose-img:rounded-xl prose-img:mx-auto prose-a:text-blue-600 " +
    "prose-pre:bg-slate-900 prose-pre:text-slate-50 prose-pre:rounded-lg " +
    "prose-code:px-1 prose-code:py-0.5 prose-code:bg-slate-100 prose-code:rounded";

  return (
    <div
      className={cx(proseDefaults, className)}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}
